import { Box, Breadcrumbs, Link, MenuItem, Skeleton, Stack } from '@mui/material';
import { type ReactNode, useState } from 'react';
import { Link as ReactRouterLink, type LinkProps as ReactRouterLinkProps } from 'react-router-dom';

import { isDefined } from '../../utils/types.js';
import { ButtonAndMenu } from '../ButtonAndMenu/ButtonAndMenu.js';
import { SpanWithEllipsisAndTooltipOnOverflow } from '../SpanWithEllipsisAndTooltipOnOverflow.js';
import { HelpTooltip } from '../Tooltips/Help.js';
import { Text } from '../Typography';

interface BaseBreadcrumbLinkProps {
  to: ReactRouterLinkProps['to'];
  isPending?: boolean;
  helperText?: ReactNode;
}

type BreadcrumbLinkProps = BaseBreadcrumbLinkProps &
  (
    | {
        label: string;
      }
    | {
        key: string;
        label: ReactNode;
      }
  );

export interface NotableBreadcrumbProps {
  links?: BreadcrumbLinkProps[];
  activeRoute: { label: string; isPending?: boolean; helperText?: ReactNode };
}

export function NotableBreadcrumb(props: NotableBreadcrumbProps) {
  const { links, activeRoute } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Show first route in menu only for routes 3+ levels deep (including active route)
  const allLinks = links ?? [];
  const totalLevels = allLinks.length + 1; // +1 for active route
  const isDeepRoute = totalLevels >= 3;
  const firstLink = allLinks[0];
  const otherLinks = isDeepRoute ? allLinks.slice(1) : allLinks;

  return (
    <Breadcrumbs
      sx={{
        '& .MuiBreadcrumbs-ol': {
          flexWrap: 'nowrap',
        },
        '& .MuiBreadcrumbs-li:last-child': {
          flex: 1,
          minWidth: 0,
        },
      }}
    >
      {/* Menu as first item - show first route in menu only for 3+ levels deep */}
      {isDeepRoute && isDefined(firstLink) ? (
        firstLink.isPending ? (
          <Skeleton width={50} />
        ) : (
          <ButtonAndMenu aria-label="More routes" anchorEl={anchorEl} setAnchorEl={setAnchorEl}>
            <MenuItem>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                width="fit-content"
                component="span"
              >
                <Link
                  component={ReactRouterLink}
                  to={firstLink.to}
                  underline="none"
                  color="inherit"
                >
                  {firstLink.label}
                </Link>
                {isDefined(firstLink.helperText) && (
                  <HelpTooltip tooltipContent={firstLink.helperText} />
                )}
              </Stack>
            </MenuItem>
          </ButtonAndMenu>
        )
      ) : null}

      {/* Other routes - show all other links as plain breadcrumb items */}
      {otherLinks.map((link) => (
        <Text key={'key' in link ? link.key : link.label} variant="heading3" bold>
          {link.isPending ? (
            <Skeleton width={120} />
          ) : (
            <Stack direction="row" alignItems="center" gap={1} width="fit-content" component="span">
              <Link component={ReactRouterLink} to={link.to}>
                {link.label}
              </Link>
              {isDefined(link.helperText) && <HelpTooltip tooltipContent={link.helperText} />}
            </Stack>
          )}
        </Text>
      ))}

      {/* Active route */}
      <Text variant="heading3" bold noWrap>
        {activeRoute.isPending ? (
          <Skeleton width={120} />
        ) : (
          <Stack direction="row" alignItems="center" gap={1} component="span" sx={{ minWidth: 0 }}>
            <Box component="span" sx={{ minWidth: 0, flexShrink: 1 }}>
              <SpanWithEllipsisAndTooltipOnOverflow>
                {activeRoute.label}
              </SpanWithEllipsisAndTooltipOnOverflow>
            </Box>
            {isDefined(activeRoute.helperText) && (
              <HelpTooltip tooltipContent={activeRoute.helperText} />
            )}
          </Stack>
        )}
      </Text>
    </Breadcrumbs>
  );
}
